<template>
  <div class="rd-month">
    <div v-if="!employee" class="rd-empty">
      <b>{{ $t("report_desktop.pick_worker") }}</b>
    </div>

    <template v-else>
      <div v-if="!compact" class="rd-detail__head">
        <span class="ui-avatar ui-avatar--neutral ui-avatar--lg">{{ initials }}</span>
        <div>
          <h2>{{ employee.fullName }}</h2>
          <span class="rd-detail__sub">{{ subtitle }}</span>
        </div>
        <span class="rd-detail__spacer"></span>
        <button type="button" class="ui-btn ui-btn--sm" @click="print">
          <v-icon size="16">mdi-printer-outline</v-icon>
          {{ $t("print") }}
        </button>
      </div>

      <div class="rd-detail__body">
        <div class="rd-month-nav">
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm"
            :aria-label="$t('report_mobile.prev_month')"
            @click="stepMonth(-1)"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
          <div class="rd-month-nav__title" aria-live="polite">
            {{ monthTitle }}
            <small v-if="monthHint">{{ monthHint }}</small>
          </div>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm"
            :aria-label="$t('report_mobile.month_next')"
            @click="stepMonth(1)"
          >
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
        </div>

        <div class="rd-stats rd-stats--pair">
          <div class="ui-stat ui-stat--inverse">
            <span class="ui-stat__label">{{ $t("report_mobile.stat_orders") }}</span>
            <span class="ui-stat__value">{{ totals.orders }}</span>
          </div>
          <div class="ui-stat">
            <span class="ui-stat__label">{{ $t("report_mobile.stat_dishes") }}</span>
            <span class="ui-stat__value">{{ totals.dishes }}</span>
          </div>
        </div>

        <div v-if="loading" class="rd-skeleton" style="height: 220px"></div>
        <div v-else-if="failed" class="rd-alert ui-banner ui-banner--lg" role="alert">
          <v-icon size="22" color="#7a1b13">mdi-alert-outline</v-icon>
          <div>
            <b>{{ $t("report_mobile.load_failed") }}</b>
            <button type="button" class="ui-btn ui-btn--sm rd-state__retry" @click="loadMonth">
              {{ $t("report_mobile.retry") }}
            </button>
          </div>
        </div>

        <template v-else>
          <div class="rd-heat" role="group" :aria-label="$t('report_desktop.month_overview')">
            <span v-for="(weekday, index) in weekdays" :key="`wd-${index}`" class="rd-heat__wd">
              {{ weekday }}
            </span>
            <template v-for="(cell, index) in cells">
              <span v-if="!cell" :key="`blank-${index}`" class="rd-heat__day is-blank"></span>
              <span
                v-else
                :key="cell.key"
                class="rd-heat__day"
                :class="cell.classes"
                :title="cell.title"
                :role="cell.clickable ? 'button' : null"
                :tabindex="cell.clickable ? 0 : null"
                @click="selectDay(cell)"
                @keydown.enter.prevent="selectDay(cell)"
                @keydown.space.prevent="selectDay(cell)"
              >
                <b>{{ cell.day }}</b>
                <span>{{ cell.text }}</span>
              </span>
            </template>
          </div>

          <div class="rd-month__tools">
            <report-checklist-menu
              v-if="showRestaurantFilter"
              v-model="selectedRestaurants"
              :label="$t('restaurants')"
              :items="restaurantItems"
              icon="mdi-silverware-fork-knife"
            ></report-checklist-menu>
            <label class="rd-live rd-month__all">
              <input v-model="showAllDays" type="checkbox" class="rd-switch" role="switch" />
              {{ $t("show_all_days") }}
            </label>
          </div>

          <ul v-if="days.length" class="rd-days">
            <li
              v-for="day in days"
              :key="day.key"
              class="rd-day"
              :class="{ 'is-selected': day.key === selectedKey }"
              :data-day="day.key"
            >
              <span class="rd-day__tile" :class="day.tileClass">
                <b class="rd-num">{{ day.num }}</b>
                <span>{{ day.weekday }}</span>
              </span>
              <span>
                <template v-if="day.items.length">
                  <span v-for="item in day.items" :key="item.key" class="rd-day__meal">
                    {{ item.quantity }}× {{ item.name }}
                  </span>
                  <span class="rd-day__meta">{{ day.detail }}</span>
                </template>
                <span v-else class="rd-day__none">{{ day.emptyText }}</span>
              </span>
            </li>
          </ul>
          <div v-else class="rd-empty rd-empty--tight">
            <b>{{ $t("report_mobile.no_orders_month") }}</b>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import hasEmployeeMonth, { ISO } from "../../shared/hasEmployeeMonth";
import ReportChecklistMenu from "../ReportChecklistMenu.vue";

/**
 * Radnik – mesečni pregled na računaru: brojke, mreža meseca (po danima) i spisak narudžbi. Kad radnik jede u više
 * restorana, izbor "Restorani" sužava brojke, mrežu, spisak i štampu na izabrane restorane.
 * Radnik dolazi kao prop (u listi radnika desno, u panelu iz "Preuzeto"); štampa se dugmetom,
 * a ne globalnom štampom, da se ne otvori dva puta pored liste radnika.
 */
export default {
  name: "ReportDesktopWorkerMonth",
  components: { ReportChecklistMenu },
  mixins: [hasEmployeeMonth],
  props: {
    worker: {
      type: Object,
      default: null,
    },
    /** U panelu naslov radnika već stoji u zaglavlju panela */
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /** Dan izabran klikom u mreži meseca: istaknut je i u spisku desno */
      selectedKey: "",
    };
  },
  computed: {
    employee() {
      return this.worker;
    },
    handlesGlobalPrint() {
      return false;
    },
    initials() {
      return (this.employee?.fullName || "")
        .split(" ")
        .filter(Boolean)
        .map((part) => part.charAt(0))
        .slice(0, 2)
        .join("");
    },
    subtitle() {
      return [`ID ${this.employee.id}`, this.employee.companyName].filter(Boolean).join(" · ");
    },
    weekdays() {
      const formatter = new Intl.DateTimeFormat(this.formatLocale, { weekday: "narrow" });
      // 5. januar 2026. je ponedeljak
      return Array.from({ length: 7 }, (_, index) =>
        formatter.format(new Date(2026, 0, 5 + index)).toUpperCase()
      );
    },
    /** Mreža meseca: prazna polja do prvog dana (ponedeljak je prvi), pa dan po dan. */
    cells() {
      const offset = (this.monthStart.day() + 6) % 7;
      const today = moment().format(ISO);
      const list = Array.from({ length: offset }, () => null);
      for (let day = 1; day <= this.daysInMonth; day += 1) {
        const date = this.monthStart.clone().date(day);
        const key = date.format(ISO);
        const items = this.byDate[key] || [];
        const names = items.map((item) => item.name);
        const clickable = items.length > 0 || this.showAllDays;
        list.push({
          key,
          day,
          text: names[0] ? (names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]) : "",
          title: items.map((item) => `${item.quantity}× ${item.name}`).join(", "),
          classes: {
            "is-ordered": items.length > 0,
            "is-today": key === today,
            "is-weekend": [0, 6].includes(date.day()),
            "is-clickable": clickable,
            "is-selected": key === this.selectedKey,
          },
          clickable,
        });
      }
      return list;
    },
  },
  watch: {
    month() {
      this.selectedKey = "";
    },
    employee() {
      this.selectedKey = "";
    },
  },
  methods: {
    /** Klik na dan u mreži: istakne ga u spisku i skroluje spisak do njega. */
    selectDay(cell) {
      if (!cell?.clickable) {
        return;
      }
      this.selectedKey = cell.key;
      this.$nextTick(() => {
        const row = this.$el.querySelector(`[data-day="${cell.key}"]`);
        if (row) {
          row.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
  },
};
</script>
